"use client";

import { ArrowRight, Clock, Loader, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactBlocks = [{
  icon: "phone",
  label: "ADMISSIONS",
  detail: "+233 (0) 55 777 1278",
  href: "tel:+233501234567",
  interactive: true
}, {
  icon: "mail",
  label: "ENQUIRIES",
  detail: "africabusinessschool12@gmail.com",
  href: "mailto:admissions@abis-edu.com",
  interactive: true
}, {
  icon: "clock",
  label: "OFFICE HOURS",
  detail: "Mon — Fri: 08:00 — 17:00",
  href: "",
  interactive: false
}];
function ConciergeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-primary/40 bg-primary/5 backdrop-blur-none">
        <p className="text-xs uppercase tracking-[0.2em] font-mono text-primary mb-4">
          02 — APPLICATION RECEIVED
        </p>
        <p className="text-2xl font-default font-semibold text-foreground mb-8 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.15em] text-xs h-12 px-6 rounded-lg">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <p className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
        ENROLMENT INQUIRY
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Kwame Mensah" {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
                Professional Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="kwame@company.com" {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+233 50 000 0000" {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="arrival_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
                Intended Start Date
              </FormLabel>
              <FormControl>
                <Input type="date" {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all [color-scheme:dark]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="number_of_dogs" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
                Number of Participants
              </FormLabel>
              <FormControl>
                <Input type="number" min={1} placeholder="1" {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
              Corporate Requirements / Notes
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your specific training needs or organizational goals..." {...field} className="bg-transparent border-0 border-b border-muted/40 px-0 min-h-[160px] text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-secondary transition-all resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="journal_signup" className="mt-1 border-muted-foreground/60 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary rounded-sm" />
        <Label htmlFor="journal_signup" className="text-sm font-default text-muted-foreground leading-relaxed cursor-pointer">
          Subscribe to the ABIS Innovation Brief — monthly insights on AI, leadership, and African business growth.
        </Label>
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group w-full h-14 bg-secondary text-secondary-foreground hover:bg-secondary/90 uppercase tracking-[0.2em] text-sm font-mono font-semibold transition-all rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Submit Inquiry
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function BeginYourTransformation() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3969" id="begin-your-transformation" className="dark bg-background text-foreground relative overflow-hidden pt-32 md:pt-40 lg:pt-[160px] pb-24 md:pb-32 px-6 md:px-10 lg:px-16">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 blur-[140px] pointer-events-none" aria-hidden="true" />

      <div className="max-w-[1440px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] uppercase tracking-[0.3em] font-mono text-primary mb-8">
              01 — ADMISSIONS
            </p>

            <h2 className="font-serif font-normal text-foreground text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[0.95] tracking-tight mb-10">
              START YOUR
              <br />
              JOURNEY<span className="text-secondary">.</span>
            </h2>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed mb-14 max-w-md">
              Our admissions team is ready to help you select the right programme for your professional advancement.
            </p>

            <div className="space-y-0">
              {contactBlocks.map((block, i) => <div data-index={i} key={i}>
                  {i === 0 && <Separator className="bg-border" />}
                  <div className="py-7">
                    <div className="flex items-center gap-3 mb-3">
                      {block.icon === "phone" && <Phone className="size-5 text-secondary" strokeWidth={1.5} />}
                      {block.icon === "mail" && <Mail className="size-5 text-secondary" strokeWidth={1.5} />}
                      {block.icon === "clock" && <Clock className="size-5 text-secondary" strokeWidth={1.5} />}
                      <p className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">
                        {block.label}
                      </p>
                    </div>
                    {block.interactive ? <Link to={block.href} className="group inline-flex items-center gap-2 font-default font-medium text-xl md:text-[20px] text-foreground hover:text-secondary transition-colors">
                        <span>{block.detail}</span>
                        <ArrowRight className="size-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-secondary" />
                      </Link> : <p className="font-default font-medium text-xl md:text-[20px] text-foreground">{block.detail}</p>}
                  </div>
                  <Separator className="bg-border" />
                </div>)}
            </div>

            <div className="mt-12">
              <p className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground mb-3">
                CAMPUS LOCATION
              </p>
              <p className="font-default text-base text-foreground/80 leading-relaxed">Movenpick Ambassador Hotel, Presidential Floor<br />Accra, Ghana</p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative bg-card backdrop-blur-none border-l-4 border-primary p-8 md:p-12 lg:p-[60px]" style={{
            boxShadow: "20px 20px 0px 0px hsla(0, 75%, 35%, 0.1)"
          }}>
              <FormProvider formKey="lead_form_home" formId="lead_form_home" sectionName="reach-the-resort" defaultValues={{
              name: "",
              email: "",
              phone: "",
              arrival_date: "",
              number_of_dogs: "",
              special_requests: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 100
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              arrival_date: {
                required: true
              },
              number_of_dogs: {
                required: true,
                min: 1
              },
              special_requests: {
                required: false
              }
            }} submitText="Submit Inquiry" successMessage="Thank you. Your inquiry has been received — our admissions team will contact you within 24 hours." errorMessage="We could not deliver your message. Please try again or contact our admissions office directly.">
                <ConciergeForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}